import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Mail, Phone, Calendar, Edit } from 'lucide-react';
import { clientsAPI } from '@/api/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import ActivityDetailDialog from '@/components/ActivityDetailDialog';
import { getActivityPill, getActionMeta, formatShortDate } from '@/lib/activity';

const ClientDetail = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [history, setHistory] = useState([]);         // actions (call/email/sms)
  const [statusHistory, setStatusHistory] = useState([]); // Etat transitions
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchClientDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const fetchClientDetails = async () => {
    try {
      const [clientRes, historyRes, statusRes] = await Promise.all([
        clientsAPI.getOne(clientId),
        clientsAPI.getHistory(clientId),
        clientsAPI.getStatusHistory(clientId),
      ]);
      setClient(clientRes.data);
      setHistory(historyRes.data);
      setStatusHistory(statusRes.data);
    } catch (error) {
      toast.error('Impossible de charger la fiche client');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-muted-foreground">Chargement…</p>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-muted-foreground">Client introuvable</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/clients')} data-testid="back-button">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Retour
        </Button>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold font-heading tracking-tight text-foreground">
            {client.name}
          </h1>
          <p className="text-muted-foreground mt-2 leading-relaxed">Fiche client</p>
        </div>
        <Button
          onClick={() => navigate('/clients')}
          className="bg-primary text-white hover:bg-primary/90 rounded-full px-6 py-2 font-medium"
          data-testid="edit-button"
        >
          <Edit className="mr-2 h-4 w-4" />
          Modifier
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="border border-border/60">
          <CardHeader>
            <CardTitle className="font-heading text-xl">Coordonnées</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <Mail className="text-primary" size={20} />
              <div>
                <p className="text-sm text-muted-foreground">Email</p>
                <p className="font-medium text-foreground">{client.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Phone className="text-primary" size={20} />
              <div>
                <p className="text-sm text-muted-foreground">Téléphone</p>
                <p className="font-medium text-foreground">{client.phone}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Calendar className="text-primary" size={20} />
              <div>
                <p className="text-sm text-muted-foreground">Créé le</p>
                <p className="font-medium text-foreground">
                  {new Date(client.created_at).toLocaleDateString('fr-FR')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action history */}
        <Card className="border border-border/60 lg:col-span-2">
          <CardHeader>
            <CardTitle className="font-heading text-xl">Historique</CardTitle>
          </CardHeader>
          <CardContent>
            {history.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                Aucune action pour ce client pour le moment.
              </p>
            ) : (
              <div className="divide-y divide-border/60">
                {history.map((entry) => {
                  const pill = getActivityPill(entry.display_status, entry.action_type);
                  const meta = getActionMeta(entry.action_type);
                  return (
                    <button
                      key={entry.id}
                      onClick={() => setSelectedEntry(entry)}
                      className="w-full flex items-center justify-between gap-4 py-3 text-left hover:bg-muted/40 rounded-md px-2 -mx-2 transition-colors"
                      data-testid="client-history-row"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-lg">{meta.emoji}</span>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">
                            {meta.label}
                            {entry.automation_name && (
                              <span className="text-muted-foreground font-normal"> · {entry.automation_name}</span>
                            )}
                          </p>
                          <p className="text-xs text-muted-foreground">{formatShortDate(entry.date)}</p>
                        </div>
                      </div>
                      <span className={`inline-flex items-center gap-1.5 text-sm font-medium shrink-0 ${pill.text}`}>
                        <span className={`h-2 w-2 rounded-full ${pill.dot}`} />
                        {pill.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Status history */}
      <Card className="border border-border/60">
        <CardHeader>
          <CardTitle className="font-heading text-xl">Historique des statuts</CardTitle>
        </CardHeader>
        <CardContent>
          {statusHistory.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">Aucun changement de statut</p>
          ) : (
            <div className="space-y-3">
              {statusHistory.map((item) => (
                <div key={item.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div>
                    <p className="font-medium text-foreground">
                      {item.old_status || 'Initial'} → {item.new_status}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(item.changed_at).toLocaleString('fr-FR')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {client.notes && (
        <Card className="border border-border/60">
          <CardHeader>
            <CardTitle className="font-heading text-xl">Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-foreground leading-relaxed">{client.notes}</p>
          </CardContent>
        </Card>
      )}

      <ActivityDetailDialog entry={selectedEntry} onClose={() => setSelectedEntry(null)} />
    </motion.div>
  );
};

export default ClientDetail;
